import { CircleX, Lock, ShieldCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { ColorRing } from "react-loader-spinner";
import { useMutation } from "react-query";
import { POST } from "../../api/requests";

export default function PlatformLogin({ platform, toast }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loadingText, setLoadingText] = useState("Authenticating...");

  const toastSuccess = (
    <div className="text-white text-sm font-medium space-x-2 flex items-center">
      <ShieldCheck size={16} color="#80ee11" strokeWidth={2.25} />{" "}
      <p>Successfully connected to {platform.name}</p>
    </div>
  );

  const toastError = (
    <div className="text-white text-sm font-medium space-x-2 flex items-center">
      <CircleX color="#ff582e" size={16} />{" "}
      <p>Error connecting to {platform.name}</p>
    </div>
  );

  const {
    mutate: authenticatePlatform,
    isLoading,
    error,
  } = useMutation(async (platformData) => {
    try {
      const response = await POST("/platforms/auth", {
        platform: platform.platformName,
        authType: "browser",
        note: `${platform.name} credentials`,
        credentialPayload: {
          username,
          password,
        },
      });
      if (!response.ok) {
        toast(toastError);
        throw new Error("Authentication failed");
      } else {
        toast(toastSuccess);
      }
      return response.json();
    } catch (e) {
      toast(toastError);
    }
  });

  useEffect(() => {
    if (isLoading) {
      const messages = [
        "Authenticating...",
        "Validating credentials...",
        "Setting up connection...",
      ];
      let currentIndex = 0;
      const intervalId = setInterval(() => {
        if (currentIndex < messages.length) {
          setLoadingText(messages[currentIndex]);
          currentIndex++;
        } else {
          clearInterval(intervalId);
        }
      }, 6000);
      return () => clearInterval(intervalId);
    }
  }, [isLoading]);

  return (
    <div className="p-3 px-4">
      <label
        htmlFor="username"
        className="block text-xs font-medium text-slate-700"
      >
        Username
      </label>
      <input
        id="username"
        name="username"
        disabled={isLoading}
        type="text"
        autoComplete="username"
        required
        className={`mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-800 sm:text-sm sm:leading-6 ${
          isLoading ? "opacity-60" : ""
        }`}
        placeholder="Enter your username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <label
        htmlFor="password"
        className="block mt-4 text-xs font-medium text-slate-700"
      >
        Password
      </label>
      <input
        disabled={isLoading}
        id="password"
        name="password"
        type="password"
        autoComplete="current-password"
        required
        className={`mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-800 sm:text-sm sm:leading-6 ${
          isLoading ? "opacity-60" : ""
        }`}
        placeholder="Enter your password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      <button
        onClick={() => authenticatePlatform({ username, password, platform })}
        style={{
          backgroundColor: isLoading ? "#f1f5f9" : "#C2FB20",
          color: "#0F172A",
        }}
        className="mt-6 py-2.5 flex hover:bg-opacity-70 items-center justify-center space-x-1.5 font-medium text-xs px-2.5 rounded-md w-full"
        disabled={isLoading}
      >
        {isLoading ? (
          <div className="flex items-center space-around space-x-2">
            <ColorRing
              visible={true}
              height="16"
              width="16"
              ariaLabel="blocks-loading"
              colors={["#0F172A", "#0F172A", "#0F172A", "#0F172A", "#0F172A"]}
            />
            <p>{loadingText}</p>
          </div>
        ) : (
          <div className="flex space-around items-center space-x-2">
            <Lock strokeWidth={2.5} size={12} />
            <p>Securely connect to {platform.name}</p>
          </div>
        )}
      </button>

      <div className="py-2 mt-1">
        <p className="text-xs text-slate-500 text-center">
          We'll establish a connection to {platform.name} using encrypted
          credentials
        </p>
        <p className="text-xs text-slate-400 text-center underline">
          Learn more about our security
        </p>
      </div>
    </div>
  );
}
